import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../context/LangContext";
import { SITE } from "../lib/site";

const HEADER_INITIAL = { y: -80, opacity: 0 };
const HEADER_ANIMATE = { y: 0, opacity: 1 };
const HEADER_TRANSITION = { duration: 1, ease: [0.16, 1, 0.3, 1] };

const Brand = ({ path }) => (
  <Link to={path("home")} data-testid="logo-link" className="flex flex-col leading-none min-w-0">
    <span className="font-serif text-base sm:text-lg md:text-xl text-cream tracking-wide truncate">Maria João Nepomuceno</span>
    <span className="text-[9px] tracking-[0.3em] uppercase text-gold mt-1 truncate">Advogada · {SITE.cedula}</span>
  </Link>
);

const DesktopNav = ({ t, path, goSection }) => (
  <nav className="hidden lg:flex items-center gap-9">
    <a href="#profile" onClick={goSection("profile")} data-testid="nav-profile" className="link-underline btn-caps text-sand hover:text-cream transition-colors">{t.nav.profile}</a>
    <a href="#areas" onClick={goSection("areas")} data-testid="nav-areas" className="link-underline btn-caps text-sand hover:text-cream transition-colors">{t.nav.areas}</a>
    <Link to={path("historia")} data-testid="nav-story" className="link-underline btn-caps text-sand hover:text-cream transition-colors">{t.nav.story}</Link>
    <Link to={path("guia")} data-testid="nav-guide" className="link-underline btn-caps text-sand hover:text-cream transition-colors">{t.nav.guide}</Link>
  </nav>
);

const LangToggle = ({ lang, toggle }) => (
  <button onClick={toggle} data-testid="lang-toggle" aria-label="Toggle language"
    className="btn-caps text-sand hover:text-gold transition-colors flex items-center gap-1">
    <span className={lang === "pt" ? "text-gold" : ""}>PT</span>
    <span className="text-sand/40">/</span>
    <span className={lang === "en" ? "text-gold" : ""}>EN</span>
  </button>
);

const MobileMenu = ({ t, path, goSection }) => (
  <nav data-testid="mobile-menu" className="lg:hidden border-t border-gold/10 bg-ink/95 backdrop-blur-xl">
    <div className="container-lux py-6 flex flex-col gap-5">
      <a href="#profile" onClick={goSection("profile")} data-testid="mobile-nav-profile" className="btn-caps text-sand hover:text-cream transition-colors">{t.nav.profile}</a>
      <a href="#areas" onClick={goSection("areas")} data-testid="mobile-nav-areas" className="btn-caps text-sand hover:text-cream transition-colors">{t.nav.areas}</a>
      <Link to={path("historia")} data-testid="mobile-nav-story" className="btn-caps text-sand hover:text-cream transition-colors">{t.nav.story}</Link>
      <Link to={path("guia")} data-testid="mobile-nav-guide" className="btn-caps text-sand hover:text-cream transition-colors">{t.nav.guide}</Link>
      <a href="#contact" onClick={goSection("contact")} data-testid="mobile-nav-book" className="btn-gold btn-caps inline-flex self-start !py-2.5 !px-5">{t.nav.book}</a>
    </div>
  </nav>
);

export const Header = () => {
  const { lang, t, toggle, path } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = location.pathname === path("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const goSection = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(path("home"));
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
    }
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={HEADER_INITIAL}
      animate={HEADER_ANIMATE}
      transition={HEADER_TRANSITION}
      className={`fixed top-0 inset-x-0 z-50 border-b border-gold/10 transition-colors duration-500 ${
        scrolled || open ? "bg-ink/90 backdrop-blur-xl" : "bg-ink/50 backdrop-blur-md"
      }`}
    >
      <div className="container-lux flex items-center justify-between gap-3 h-16 md:h-20">
        <Brand path={path} />
        <DesktopNav t={t} path={path} goSection={goSection} />
        <div className="flex items-center gap-3 md:gap-6 shrink-0">
          <LangToggle lang={lang} toggle={toggle} />
          <a href="#contact" onClick={goSection("contact")} data-testid="header-book-cta" className="btn-gold btn-caps !hidden md:!inline-flex !py-2.5 !px-5 whitespace-nowrap">
            {t.nav.book}
          </a>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} aria-label="Menu"
            className="lg:hidden text-cream hover:text-gold transition-colors p-1">
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>
      {open && <MobileMenu t={t} path={path} goSection={goSection} />}
    </motion.header>
  );
};
